import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeModalAction } from '../../../actions/modal';
import { useForm } from 'react-hook-form';
import Form from '../../../common/Form';
import Input from '../../../common/Form/Input';
import { userSettlement } from '../../../actions/users';
import { convertIntoPaisa, convertIntoRupee } from '../../../utils/utils';

const formName = 'userSettlement';
export default function UserSettlement({ user_id, isClusterOrPspUser = false, walletDetails }) {
    const { formButtonLoading } = useSelector((state) => state.loading);
    const dispatch = useDispatch();

    const formatAmount = (amountInPaisa) => {
        if (!amountInPaisa && amountInPaisa !== 0) return '0.00';
        const rupees = convertIntoRupee(amountInPaisa);
        return rupees.toFixed(2);
    };

    const submit = (form) => {
        const data = {
            balance_in_paisa_credit: convertIntoPaisa(form.balance_in_paisa_credit) || 0,
            balance_in_paisa_debit: convertIntoPaisa(form.balance_in_paisa_debit) || 0,
            remark: form.remark,
            currency: 'INR',
        }
        if (isClusterOrPspUser) {
            data.incentive_in_paisa = convertIntoPaisa(form.incentive_in_paisa) || 0;
            data.locked_in_paisa = convertIntoPaisa(form.locked_in_paisa) || 0;
        }
        dispatch(userSettlement({ data, formName, id: user_id, closeModal: true, refresh: false }))
    };

    const methods = useForm({
        defaultValues: {},
        mode: 'all',
        resolver: (data) => {
            const errors = {};
            const { balance_in_paisa_credit, balance_in_paisa_debit, incentive_in_paisa, locked_in_paisa, remark } = data;

            if (balance_in_paisa_credit && balance_in_paisa_debit) {
                errors.balance_in_paisa_credit = { message: 'Only one of Credit or Debit can be filled at a time' };
                errors.balance_in_paisa_debit = { message: 'Only one of Credit or Debit can be filled at a time' };
            }
            if (!balance_in_paisa_credit && !balance_in_paisa_debit && !incentive_in_paisa && !locked_in_paisa) {
                errors.balance_in_paisa_credit = { message: 'At least one field must be filled' };
                errors.balance_in_paisa_debit = { message: 'At least one field must be filled' };
                errors.incentive_in_paisa = { message: 'At least one field must be filled' };
                errors.locked_in_paisa = { message: 'At least one field must be filled' };
            }

            if (!remark) {
                errors.remark = { message: 'Remark is required' };
            }

            return {
                values: data,
                errors: Object.keys(errors).length > 0 ? errors : {}
            };
        }
    });

    const { watch, setValue } = methods;
    const creditValue = watch('balance_in_paisa_credit');
    const debitValue = watch('balance_in_paisa_debit');

    const handleCreditChange = (value) => {
        if (value && debitValue) {
            setValue('balance_in_paisa_debit', '');
        }
    };

    const handleDebitChange = (value) => {
        if (value && creditValue) {
            setValue('balance_in_paisa_credit', '');
        }
    };

    return (
        <Form
            methods={methods}
            onSubmit={submit}
            submitButtonText={'Submit'}
            onCancel={() => dispatch(closeModalAction())}
            showSubmit
            submitContainerClassName='flex gap-2 justify-end py-4 px-4'
            isLoading={formButtonLoading[formName]}
        >
            {walletDetails && (
                <div className='px-4 py-3 bg-gray-50 border-b border-gray-200'>
                    <div className='flex flex-wrap gap-2'>
                        <div className='inline-flex items-center px-4 py-2 rounded-full bg-blue-100 border border-blue-200'>
                            <span className='text-md font-medium text-blue-800 select-none'>Balance:</span>
                            <span
                                className='ml-2 text-md font-semibold text-blue-900 cursor-pointer select-none'
                                title="Click to copy"
                                onClick={() => {
                                    navigator.clipboard.writeText(walletDetails.balance?.toString());
                                }}
                            >
                                ₹{formatAmount(walletDetails.balance)}
                            </span>
                        </div>
                        {isClusterOrPspUser && (
                            <>
                                <div className='inline-flex items-center px-4 py-2 rounded-full bg-green-100 border border-green-200'>
                                    <span className='text-md font-medium text-green-800 select-none'>Incentive:</span>
                                    <span
                                        className='ml-2 text-md font-semibold text-green-900 cursor-pointer select-none'
                                        title="Click to copy"
                                        onClick={() => {
                                            navigator.clipboard.writeText(walletDetails.incentive?.toString());
                                        }}
                                    >
                                        ₹{formatAmount(walletDetails.incentive)}
                                    </span>
                                </div>
                                <div className='inline-flex items-center px-4 py-2 rounded-full bg-amber-100 border border-amber-200'>
                                    <span className='text-md font-medium text-amber-800 select-none'>Locked:</span>
                                    <span
                                        className='ml-2 text-md font-semibold text-amber-900 cursor-pointer select-none'
                                        title="Click to copy"
                                        onClick={() => {
                                            navigator.clipboard.writeText(walletDetails.locked?.toString());
                                        }}
                                    >
                                        ₹{formatAmount(walletDetails.locked)}
                                    </span>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            <div className='px-3 justify-between flex gap-4 py-2'>
                <Input
                    className='w-full'
                    label='Credit In Wallet'
                    name='balance_in_paisa_credit'
                    type={'number'}
                    onChange={(e) => handleCreditChange(e.target.value)}
                />
                <Input
                    className='w-full'
                    label='Debit In Wallet'
                    name='balance_in_paisa_debit'
                    type={'number'}
                    onChange={(e) => handleDebitChange(e.target.value)}
                />
                {isClusterOrPspUser ? <Input className='w-full' label='Debit In Incentive' name='incentive_in_paisa' type={'text'} /> : null}
                {isClusterOrPspUser ? <Input className='w-full' label='Debit In Locked' name='locked_in_paisa' type={'text'} /> : null}
            </div>
            <div className='px-3'>
                <Input required className='w-full' label='Remark' name='remark' type={'text'} />
            </div>
        </Form>
    );
}
